export const FINANCE_CURRENCY_CODE = 'INR';

export type FinanceInputs = {
  total: string;
  paid: string;
  pending: string;
};

export type FinanceAmounts = {
  financeTotalAmount?: number;
  financePaidAmount?: number;
  financePendingAmount?: number;
};

export type FinanceUpdatePayload = {
  financeTotalAmount?: number | null;
  financePaidAmount?: number | null;
  financePendingAmount?: number | null;
};

export function emptyFinanceInputs(): FinanceInputs {
  return { total: '', paid: '', pending: '' };
}

export function financeInputsFromRecord(
  record?: FinanceAmounts | null,
): FinanceInputs {
  const total =
    record?.financeTotalAmount != null
      ? formatFinanceAmount(record.financeTotalAmount)
      : '';
  const paid =
    record?.financePaidAmount != null ? formatFinanceAmount(record.financePaidAmount) : '';
  const calculatedPending = calculateFinancePending(total, paid);
  return {
    total,
    paid,
    pending:
      calculatedPending ||
      (record?.financePendingAmount != null
        ? formatFinanceAmount(clampFinancePendingAmount(record.financePendingAmount))
        : ''),
  };
}

export function financeUpdatePayloadFromInputs(inputs: FinanceInputs): FinanceUpdatePayload {
  return {
    financeTotalAmount: parseFinanceInput(inputs.total),
    financePaidAmount: parseFinanceInput(inputs.paid),
    financePendingAmount: parseFinanceInput(inputs.pending),
  };
}

export function financeAmountsEqual(
  left?: FinanceAmounts | null,
  right?: FinanceAmounts | FinanceUpdatePayload | null,
): boolean {
  return (
    normalizeFinanceAmount(left?.financeTotalAmount) ===
      normalizeFinanceAmount(right?.financeTotalAmount) &&
    normalizeFinanceAmount(left?.financePaidAmount) ===
      normalizeFinanceAmount(right?.financePaidAmount) &&
    normalizeFinanceAmount(left?.financePendingAmount) ===
      normalizeFinanceAmount(right?.financePendingAmount)
  );
}

export function financeInputsMatchRecord(
  inputs: FinanceInputs,
  record?: FinanceAmounts | null,
): boolean {
  const saved = financeInputsFromRecord(record);
  return (
    normalizeFinanceInputText(inputs.total) === normalizeFinanceInputText(saved.total) &&
    normalizeFinanceInputText(inputs.paid) === normalizeFinanceInputText(saved.paid) &&
    normalizeFinanceInputText(inputs.pending) === normalizeFinanceInputText(saved.pending)
  );
}

export function normalizeFinanceFieldValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function calculateFinancePending(total: string, paid: string): string {
  const totalAmount = parseFinanceInput(total);
  const paidAmount = parseFinanceInput(paid);
  if (totalAmount === null || paidAmount === null) return '';
  return formatFinanceAmount(clampFinancePendingAmount(totalAmount - paidAmount));
}

export function clampFinancePendingAmount(value: number): number {
  return roundFinanceAmount(Math.max(0, value));
}

export function roundFinanceAmount(value: number): number {
  return Math.round(value * 100) / 100;
}

export function formatFinanceAmount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return roundFinanceAmount(value).toFixed(2);
}

export function formatFinanceAmountInr(value: number | null | undefined): string {
  const formatted = formatFinanceAmount(value);
  if (!formatted) return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: FINANCE_CURRENCY_CODE,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(formatted));
}

function parseFinanceInput(value: string | number | null | undefined): number | null {
  const trimmed = normalizeFinanceFieldValue(value);
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return roundFinanceAmount(parsed);
}

function normalizeFinanceInputText(value: string | number | null | undefined): string {
  const trimmed = normalizeFinanceFieldValue(value);
  if (!trimmed) return '';
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return trimmed;
  return formatFinanceAmount(parsed);
}

function normalizeFinanceAmount(value?: number | null): number | null {
  return value == null ? null : value;
}
